import { useMemo, useState, type RefObject } from 'react';
import { useGT } from 'gt-react';
import type { As400ExportGroup } from '@erp/rpc';
import { useToast } from '@/context/ToastContext';
import type { PendingEditsByRowKey } from './as400ExportEditState';
import {
  reviewCellErrors,
  reviewErrorKey,
  type EditedRow,
  type ErrorScope,
  type ReviewCellError,
} from './as400ReviewErrors';
import type { CellRowLookups } from '@erp/item-master-rules';
import {
  columnNamer,
  describeCellViolation,
} from './as400CellViolationMessages';
import type { GoToWorksheetCell } from './useWorksheetCellJump';

/** Where the review's errors come from, and how to open another worksheet. */
export interface ReviewErrorNavigation {
  /** Every worksheet of the review, in tab order. */
  scopes: readonly ErrorScope[];
  /** Show `group`'s worksheet at `page` (the current one, or another). */
  onOpen: (group: As400ExportGroup, page: number) => void;
}

/** Selects one row's cell in the grid on screen; false when it isn't there. */
interface CellSelector {
  selectCell: (rowKey: string, field: string) => boolean;
}

/**
 * The review's error summary state (GEN-2747): the errors across every
 * worksheet's unsaved edits, which one was last jumped to, and the jump
 * itself — through the grid's open-then-select ({@link GoToWorksheetCell}),
 * which opens another worksheet or page first. A cell that can't be selected
 * — its row filtered or sorted off the page, or hidden — is said in a popup
 * instead.
 */
export function useReviewErrorNavigation({
  navigation,
  pendingByScope,
  editedRow,
  takenItemNumbers,
  goToCell,
  grid,
  lookups,
}: {
  navigation: ReviewErrorNavigation | undefined;
  pendingByScope: ReadonlyMap<string, PendingEditsByRowKey>;
  editedRow: (rowKey: string) => EditedRow | undefined;
  takenItemNumbers?: ReadonlySet<string>;
  goToCell: GoToWorksheetCell;
  grid: RefObject<CellSelector | null>;
  lookups?: CellRowLookups;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const scopes = navigation?.scopes;
  const errors = useMemo(
    () =>
      scopes
        ? reviewCellErrors(
            scopes,
            pendingByScope,
            editedRow,
            { takenItemNumbers },
            lookups,
          )
        : [],
    [scopes, pendingByScope, editedRow, takenItemNumbers, lookups],
  );
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const found = errors.findIndex(
    (error) => reviewErrorKey(error) === activeKey,
  );
  const activeIndex = found === -1 ? null : found;

  const notFound = (error: ReviewCellError) =>
    showToast({
      title: gt('Cell not found', {
        context: 'review error list: the invalid cell jumped to is not shown',
      }),
      message: gt(
        'Its row is not available on page {page} now: a filter, the sort or hiding the row may have made it so.',
        {
          page: error.page,
          context:
            'review error list: why the invalid cell jumped to could not be selected',
        },
      ),
      color: 'warning',
    });

  const select = (error: ReviewCellError) => {
    if (!grid.current?.selectCell(error.rowKey, error.violation.field)) {
      notFound(error);
    }
  };

  const jump = (index: number) => {
    const error = errors[index];
    if (!error || !navigation) return;
    setActiveKey(reviewErrorKey(error));
    goToCell({
      group: error.group,
      page: error.page,
      arrive: () => select(error),
    });
  };

  const namers = useMemo(
    () =>
      new Map(
        (scopes ?? []).map((scope) => [
          scope.scopeKey,
          columnNamer(scope.columns),
        ]),
      ),
    [scopes],
  );
  const describe = (error: ReviewCellError) =>
    describeCellViolation(
      gt,
      error.violation,
      namers.get(error.scopeKey) ?? ((field: string) => field),
    );

  return { errors, activeIndex, jump, describe };
}
